import { MatchExtended, MatchState } from '../model/match';
import { ControlMatch, PlayerMatchStats, CATEGORY_COLORS } from '../../tournament-control-component/models/control-state';

export interface PriorityContext {
  playerStats: Map<string, PlayerMatchStats>;
  lastMatches: MatchExtended[];
  now: number;
}

export function computePlayerStats(matches: MatchExtended[]): Map<string, PlayerMatchStats> {
  const stats = new Map<string, PlayerMatchStats>();

  for (const match of matches) {
    for (const playerId of [match.player1Id, match.player2Id]) {
      if (!playerId) continue;
      if (!stats.has(playerId)) {
        const playerName =
          (playerId === match.player1Id ? match.player1?.playerName : match.player2?.playerName) || playerId;
        stats.set(playerId, {
          playerId,
          playerName,
          matchesPlayed: 0,
          matchesInProgress: 0,
          lastMatchEndedAt: undefined,
        });
      }
    }

    const p1 = match.player1Id ? stats.get(match.player1Id) : undefined;
    const p2 = match.player2Id ? stats.get(match.player2Id) : undefined;

    if (match.state === MatchState.FINALIZADO) {
      if (p1) p1.matchesPlayed++;
      if (p2) p2.matchesPlayed++;

      const endedAt = (match as any).matchEndedAt?.toMillis?.() || 0;
      if (p1 && endedAt > (p1.lastMatchEndedAt || 0)) p1.lastMatchEndedAt = endedAt;
      if (p2 && endedAt > (p2.lastMatchEndedAt || 0)) p2.lastMatchEndedAt = endedAt;
    } else if (match.state === MatchState.EN_CANCHA || match.state === MatchState.JUGANDO) {
      if (p1) p1.matchesInProgress++;
      if (p2) p2.matchesInProgress++;
    }
  }

  return stats;
}

export function computePriority(match: ControlMatch, ctx: PriorityContext): number {
  const { playerStats, lastMatches, now } = ctx;
  const p1 = match.player1Id ? playerStats.get(match.player1Id) : undefined;
  const p2 = match.player2Id ? playerStats.get(match.player2Id) : undefined;

  if (p1?.matchesInProgress && p1.matchesInProgress > 0) return 9000;
  if (p2?.matchesInProgress && p2.matchesInProgress > 0) return 9000;

  let score = 0;

  const maxPlayed = getMaxPlayedInCategory(lastMatches, match.category || '');
  const p1Deficit = maxPlayed - (p1?.matchesPlayed || 0);
  const p2Deficit = maxPlayed - (p2?.matchesPlayed || 0);
  const avgDeficit = (p1Deficit + p2Deficit) / 2;
  score -= avgDeficit * 20;

  const p1Rest = p1?.lastMatchEndedAt ? (now - p1.lastMatchEndedAt) / 60000 : 999;
  const p2Rest = p2?.lastMatchEndedAt ? (now - p2.lastMatchEndedAt) / 60000 : 999;
  const minRest = Math.min(p1Rest, p2Rest);
  if (minRest >= 15) score -= 10;
  if (minRest >= 30) score -= 5;

  if (match.groupName !== 'finales') score -= 5;

  const scheduledMillis = getDateMillis(match.date);
  if (scheduledMillis > 0) {
    const diffMinutes = (now - scheduledMillis) / 60000;
    if (diffMinutes > 0) {
      score -= 30 + Math.min(diffMinutes / 3, 30);
    } else {
      score += (-diffMinutes) / 6;
    }
  }

  return score;
}

function getMaxPlayedInCategory(matches: MatchExtended[], category: string): number {
  const countMap = new Map<string, number>();
  for (const m of matches) {
    if (m.category === category && m.state === MatchState.FINALIZADO) {
      if (m.player1Id) countMap.set(m.player1Id, (countMap.get(m.player1Id) || 0) + 1);
      if (m.player2Id) countMap.set(m.player2Id, (countMap.get(m.player2Id) || 0) + 1);
    }
  }
  let max = 0;
  for (const [, count] of countMap) {
    if (count > max) max = count;
  }
  return max;
}

/**
 * Umbral a partir del cual un manualOrder se considera "parked at bottom":
 * el partido va al final de la cola global (después de los auto-ordenados),
 * no al final del grupo manual. Se usa por ejemplo al resetear un partido
 * que estaba en cancha y vuelve a PROGRAMADO.
 */
export const PARK_BOTTOM_THRESHOLD = 1_000_000;

export function sortByPriority(
  matches: ControlMatch[],
  manualOrder: Record<string, number>,
  ctx: PriorityContext
): ControlMatch[] {
  for (const m of matches) {
    m.priority = computePriority(m, ctx);
    m.playerAvailable = m.priority < 9000;
  }

  const manual: ControlMatch[] = [];
  const parked: ControlMatch[] = [];
  const available: ControlMatch[] = [];
  const unavailable: ControlMatch[] = [];

  for (const m of matches) {
    const mo = m.id ? manualOrder[m.id] : undefined;
    if (mo !== undefined) {
      if (mo >= PARK_BOTTOM_THRESHOLD) {
        parked.push(m);
      } else {
        manual.push(m);
      }
    } else if (m.playerAvailable) {
      available.push(m);
    } else {
      unavailable.push(m);
    }
  }

  manual.sort((a, b) => (manualOrder[a.id!] || 0) - (manualOrder[b.id!] || 0));
  parked.sort((a, b) => (manualOrder[a.id!] || 0) - (manualOrder[b.id!] || 0));
  available.sort((a, b) => a.priority - b.priority);

  const separated = separatePlayers(available);

  return [...manual, ...separated, ...parked, ...unavailable];
}

export function separatePlayers(sorted: ControlMatch[]): ControlMatch[] {
  if (sorted.length <= 2) return sorted;

  const result: ControlMatch[] = [];
  const remaining = [...sorted];

  while (remaining.length > 0) {
    const recentPlayerIds = new Set<string>();
    if (result.length > 0) {
      const last = result[result.length - 1];
      if (last.player1Id) recentPlayerIds.add(last.player1Id);
      if (last.player2Id) recentPlayerIds.add(last.player2Id);
    }

    let pickedIndex = -1;
    for (let i = 0; i < remaining.length; i++) {
      const m = remaining[i];
      const hasConflict =
        (m.player1Id && recentPlayerIds.has(m.player1Id)) ||
        (m.player2Id && recentPlayerIds.has(m.player2Id));
      if (!hasConflict) {
        pickedIndex = i;
        break;
      }
    }

    if (pickedIndex === -1) pickedIndex = 0;

    result.push(remaining.splice(pickedIndex, 1)[0]);
  }

  return result;
}

export function estimateProgress(match: MatchExtended): { percent: number; minutesLeft: number } {
  const bestOf = match.bestOf || 3;
  const pointsToWin = match.pointsToWin || 11;
  const p1GamesWon = match.player1GamesWon || 0;
  const p2GamesWon = match.player2GamesWon || 0;
  const gamesPlayed = p1GamesWon + p2GamesWon;
  const requiredWins = Math.ceil(bestOf / 2);
  const expectedGames = requiredWins + Math.min(p1GamesWon, p2GamesWon);

  let currentGameProgress = 0;
  if (match.games && match.games.length > 0) {
    const lastGame = match.games[match.games.length - 1];
    if (lastGame) {
      const maxPoints = Math.max(lastGame.player1Points || 0, lastGame.player2Points || 0);
      currentGameProgress = Math.min(maxPoints / pointsToWin, 1);
    }
  }

  const totalProgress = expectedGames > 0
    ? (gamesPlayed + currentGameProgress) / expectedGames
    : 0;
  const percent = Math.min(Math.round(totalProgress * 100), 95);

  const avgMinPerGame = 12;
  const remainingGames = expectedGames - gamesPlayed - currentGameProgress;
  const minutesLeft = Math.max(1, Math.round(remainingGames * avgMinPerGame));

  return { percent, minutesLeft };
}

export function toControlMatch(match: MatchExtended): ControlMatch {
  const cm: ControlMatch = {
    ...match,
    priority: 0,
    categoryColor: CATEGORY_COLORS[match.category || ''] || '#9E9E9E',
    playerAvailable: true,
  };

  if (match.state === MatchState.JUGANDO) {
    const progress = estimateProgress(match);
    cm.progressPercent = progress.percent;
    cm.estimatedMinutesLeft = progress.minutesLeft;
  }

  return cm;
}

export interface SplitMatches {
  enCancha: ControlMatch[];
  pendientes: ControlMatch[];
  finalizados: ControlMatch[];
}

export function splitByState(matches: MatchExtended[]): SplitMatches {
  const enCancha: ControlMatch[] = [];
  const pendientes: ControlMatch[] = [];
  const finalizados: ControlMatch[] = [];

  for (const match of matches) {
    const cm = toControlMatch(match);
    if (match.state === MatchState.EN_CANCHA || match.state === MatchState.JUGANDO) {
      enCancha.push(cm);
    } else if (match.state === MatchState.PROGRAMADO) {
      pendientes.push(cm);
    } else if (match.state === MatchState.FINALIZADO) {
      finalizados.push(cm);
    }
  }

  finalizados.sort((a, b) => {
    const dateA = (a as any).matchEndedAt?.toMillis?.() || 0;
    const dateB = (b as any).matchEndedAt?.toMillis?.() || 0;
    return dateB - dateA;
  });

  return { enCancha, pendientes, finalizados };
}

export function getDateMillis(date: any): number {
  if (!date) return 0;
  if (date instanceof Date) return date.getTime();
  if (typeof date.toMillis === 'function') return date.toMillis();
  if (typeof date.seconds === 'number') return date.seconds * 1000;
  return 0;
}
